/**
 * Lets trusted client libraries create CSP-authorized style elements.
 *
 * Reown AppKit builds its wallet chooser with Lit web components. Both Lit and
 * AppKit create styles at runtime, and a nonce cannot be applied to an element
 * somebody else constructed — so `document.createElement` is wrapped to stamp
 * one onto every `<style>` it produces.
 *
 * Under Next.js this shipped as a `beforeInteractive` inline script written by
 * the server, because it had to run before hydration. In the SPA it is an
 * ordinary module instead, imported first by the entry point: ES modules
 * evaluate depth-first in import order, so a module with no imports of its own
 * runs before anything that could create a style. That removes the server's
 * need to generate JavaScript, which is one fewer place a string can become
 * code.
 */

/**
 * The nonce the server put on this document's scripts.
 *
 * Read from the IDL property rather than `getAttribute`: browsers blank the
 * content attribute after parsing — nonce hiding, so that a CSS attribute
 * selector cannot exfiltrate it — while leaving the property intact. Selecting
 * on `script[nonce]` would still match, but reading the attribute back would
 * yield an empty string.
 */
export function currentNonce(): string {
  if (typeof document === "undefined") return "";
  const script = document.querySelector<HTMLScriptElement>('script[type="module"]');
  return script?.nonce ?? "";
}

/**
 * Installs the bridge. Idempotent, and a no-op when there is no nonce to apply.
 *
 * No nonce means either a development server with a permissive policy or a
 * deployment serving `dist/` without the shell. Neither is a case where
 * stamping an empty `nonce=""` onto every style would help: an empty nonce
 * matches no policy, so it would break styling rather than secure it.
 */
export function installStyleNonce(nonce: string = currentNonce()): void {
  if (typeof document === "undefined" || nonce === "") return;

  const globals = globalThis as { litNonce?: string; __asetquStyleNonceInstalled?: boolean };
  globals.litNonce = nonce;

  if (globals.__asetquStyleNonceInstalled) return;
  globals.__asetquStyleNonceInstalled = true;

  const createElement = document.createElement.bind(document);
  document.createElement = ((tagName: string, options?: ElementCreationOptions) => {
    const element = createElement(tagName, options);
    if (String(tagName).toLowerCase() === "style") {
      element.setAttribute("nonce", nonce);
    }
    return element;
  }) as typeof document.createElement;
}
